import { BadgeCheck, Check, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';

/**
 * Salon badges (design-spec §3 "Chips").
 *
 * - verified — icon only, success green, `role="img"` + "Verified salon".
 *   `tone="seal"` = the filled seal used on mobile.
 * - mobile   — "Comes to you". Tones:
 *     tint   accent-tint + tint border, 12/700 link (salon header, desktop)
 *     photo  surface pill over a photo, 11.5/700 link (cards); `tone="photoCheck"` adds ✓ (mobile)
 *     soft   #F0E4D4 / #7A3416 (mobile meta row)
 * - new     — "New on Salunas" (D-04): shown instead of a rating when a
 *   salon has zero reviews. Accent-tint pill with a small sparkle so it reads
 *   as intentional, not as missing data.
 */
export type BadgeKind = 'verified' | 'mobile' | 'new';
export type BadgeTone =
  | 'tick'
  | 'seal'
  | 'tint'
  | 'photo'
  | 'photoCheck'
  | 'soft';

export interface BadgeProps {
  kind: BadgeKind;
  tone?: BadgeTone;
  /** `sm` = the compact results-card variant (10.5px, padding 5×9). */
  size?: 'sm' | 'md';
  className?: string;
}

const pillBase =
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full font-sans font-bold leading-none';

export function Badge({ kind, tone, size = 'md', className }: BadgeProps) {
  const { t } = useTranslation();

  if (kind === 'verified') {
    const label = t('ui.badge.verified');
    const px = size === 'sm' ? 14 : 16;
    if (tone === 'seal') {
      return (
        <BadgeCheck
          role="img"
          aria-label={label}
          className={cn('shrink-0 fill-sl-success text-white', className)}
          style={{ width: px + 4, height: px + 4 }}
          strokeWidth={2}
        />
      );
    }
    return (
      <Check
        role="img"
        aria-label={label}
        className={cn('shrink-0 text-sl-success', className)}
        style={{ width: px, height: px }}
        strokeWidth={2.4}
      />
    );
  }

  const sizeCls =
    size === 'sm'
      ? 'px-[9px] py-[5px] text-[10.5px]'
      : 'px-[11px] py-1.5 text-[11.5px]';

  if (kind === 'mobile') {
    const resolved = tone ?? 'tint';
    const toneCls: Record<string, string> = {
      tint: 'border border-sl-accent-tint-line bg-sl-accent-tint text-sl-link text-[12px]',
      photo: 'bg-sl-surface text-sl-link',
      photoCheck: 'bg-sl-surface/[.92] text-sl-ink',
      soft: 'bg-sl-pill-mobile text-sl-link-hover',
    };
    return (
      <span className={cn(pillBase, sizeCls, toneCls[resolved], className)}>
        {resolved === 'photoCheck' && (
          <Check
            className="h-3 w-3 text-sl-accent"
            strokeWidth={2.6}
            aria-hidden="true"
          />
        )}
        {t('ui.badge.comesToYou')}
      </span>
    );
  }

  return (
    <span
      className={cn(
        pillBase,
        sizeCls,
        'border border-sl-accent-tint-line bg-sl-accent-tint text-sl-link',
        className
      )}
    >
      <Sparkles className="h-3 w-3" strokeWidth={2.2} aria-hidden="true" />
      {t('ui.badge.new')}
    </span>
  );
}
